/**
 * A company identifier as people paste it: a SIREN (9 digits) or a SIRET
 * (14 digits: the SIREN followed by the establishment's NIC). The
 * application stores the SIREN on a client; an agency's SIRET is its root's
 * SIREN plus its own NIC (docs/agences.md on the API side), so from a SIRET
 * typed on a client only the SIREN part is kept.
 */
export type SirenKind = 'empty' | 'siren' | 'siret' | 'invalid';

export interface SirenInput {
  /** The 9-digit SIREN to store, null when there is none or the input is invalid. */
  siren: string | null;
  /** The NIC part of a SIRET, when one was typed. */
  nic: string | null;
  kind: SirenKind;
}

/** Digits only, up to a SIRET's length: what the inputs keep as the person types. */
export const sirenDigits = (raw: string): string => raw.replace(/\D/g, '').slice(0, 14);

export type NicKind = 'empty' | 'nic' | 'siret' | 'mismatch' | 'invalid';

export interface NicInput {
  /** The 5-digit NIC to store on the agency, null when there is none or the input is invalid. */
  nic: string | null;
  kind: NicKind;
}

/**
 * An agency's establishment number as typed: a NIC (5 digits), or the full
 * SIRET (14 digits), which must then start with the root client's SIREN
 * when that SIREN is known.
 */
export const nicFrom = (raw: string, rootSiren: string | null): NicInput => {
  const digits = sirenDigits(raw);
  if (!digits) return { nic: null, kind: 'empty' };
  if (digits.length === 5) return { nic: digits, kind: 'nic' };
  if (digits.length === 14) {
    if (rootSiren && digits.slice(0, 9) !== rootSiren) return { nic: null, kind: 'mismatch' };
    return { nic: digits.slice(9), kind: 'siret' };
  }
  return { nic: null, kind: 'invalid' };
};

export const sirenFrom = (raw: string): SirenInput => {
  const digits = sirenDigits(raw);
  if (!digits) return { siren: null, nic: null, kind: 'empty' };
  if (digits.length === 9) return { siren: digits, nic: null, kind: 'siren' };
  if (digits.length === 14) return { siren: digits.slice(0, 9), nic: digits.slice(9), kind: 'siret' };
  return { siren: null, nic: null, kind: 'invalid' };
};
